import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ImageData, FontType, defaultFont, FrameType, defaultFrame } from '../redux/cardAttributeModelIndex';
import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

type ShapeBase = {
  cardId: string;
  cardTemplateShapeId?: string;
  name: string;
  x: number;
  y: number;
  z: number;
  width: number;
  height: number;
  rotation: number;
  frame: FrameType;
  background: { display: boolean; color: string; };
  goodsId?: string;
  version?: number;
}
export type CardTextShape = ShapeBase & {
  kind: 'text';
  text: string;
  font: FontType;
};
export type CardCircleShape = ShapeBase & {
  kind: 'circle';
  stroke: {display: boolean, color: string, opacity: number, width: number};
  fill: {display: boolean, color: string, opacity: number};
};
export type CardRectangleShape = ShapeBase & {
  kind: 'rectangle';
  stroke: {display: boolean, color: string, opacity: number, width: number};
  fill: {display: boolean, color: string, opacity: number};
};
export type CardBalloonShape = ShapeBase & {
  kind: 'balloon';
  stroke: {display: boolean, color: string, opacity: number, width: number};
  fill: {display: boolean, color: string, opacity: number};
};
export type CardImageShape = ShapeBase & {
  kind: 'image';
  image: ImageData;
};
export type CardShapeDb = CardTextShape | CardRectangleShape | CardCircleShape | CardBalloonShape | CardImageShape;

const initialBase: ShapeBase = {
  cardId: '',
  cardTemplateShapeId: undefined,
  name: '',
  x: 0,
  y: 0,
  z: 0,
  width: 0,
  height: 0,
  rotation: 0,
  frame: defaultFrame,
  background: { display: false, color: '#ffffff' },
  goodsId: undefined,
  version: undefined
}
type CardShapeInitialType = CardShapeDb[]
const initial: CardShapeInitialType = [
  {
    ...initialBase,
    kind: 'text',
    text: '',
    font: defaultFont,
  },
  {
    ...initialBase,
    kind: 'rectangle',
    stroke: {display: false, color: '', opacity: 0, width: 0},
    fill: {display: false, color: '', opacity: 0}
  },
  {
    ...initialBase,
    kind: 'circle',
    stroke: {display: false, color: '', opacity: 0, width: 0},
    fill: {display: false, color: '', opacity: 0}
  },
  {
    ...initialBase,
    kind: 'balloon',
    stroke: {display: false, color: '', opacity: 0, width: 0},
    fill: {display: false, color: '', opacity: 0}
  },
  {
    ...initialBase,
    kind: 'image',
    image: {
      imageId: '',
      originWidth: 0,
      originHeight: 0,
      trimXFrom: 0,
      trimYFrom: 0,
      trimXTo: 0,
      trimYTo: 0
    }
  },
]

export type CardShapeDbHashType = {
  [key in string]: CardShapeDb;
}

export type CardShapeAddPayload = {
  id?: string;
} & CardShapeDb;
export type CardShapeUpdatePayload = {
  id: string;
} & {[P in keyof CardShapeDb]?: CardShapeDb[P]};

export type CardShapeDbState = {
  cardShapes: CardShapeDbHashType;
}

const isCardShapeDb = (obj: any): obj is CardShapeDb => {
  return 'kind' in obj && 'cardId' in obj;
}
export const isImageShape = (obj: any): obj is CardImageShape => {
  return 'kind' in obj && obj.kind === 'image' && 'cardId' in obj;
}
const isSameType = <T, _>(a: T, b: any): b is T => {
  return isCardShapeDb(a) && a.kind === b?.kind;
}

export const generateCardShapeId = (): string => generateId('cs');
let initialState: CardShapeDbState = { cardShapes: {} };
const key = 'cardShapes';

const cardShapeDbSlice = createSlice({
  name: 'cardShapeDb',
  initialState,
  reducers: {
    initializeCardShapes(state: CardShapeDbState) {
      state.cardShapes = {};
    },
    addCardShape(state: CardShapeDbState, action: PayloadAction<CardShapeAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateCardShapeId();
      state.cardShapes[id] = obj;
      saveData(key, id, obj);
    },
    updateCardShape(state: CardShapeDbState, action: PayloadAction<CardShapeUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.cardShapes[id] === undefined) {
        console.log(`*** CardShape not found: ${id}`);
      } else if (isSameType(state.cardShapes[id], obj)) {
        const newData = { ...state.cardShapes[id], ...obj };
        state.cardShapes[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteCardShape(state: CardShapeDbState, action: PayloadAction<CardShapeUpdatePayload>) {
      const id = action.payload.id;
      delete state.cardShapes[id];
      deleteData(key, id);
    },
    saveCardShapes(state: CardShapeDbState) {
      saveAllData(key, state.cardShapes);
    },
    signUpCardShapes(state: CardShapeDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.cardShapes, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: CardShapeDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.cardShapes = updatedValues;
      if (changed) saveAllData(key, state.cardShapes);
    })
  }
})

export const { initializeCardShapes, addCardShape, updateCardShape, deleteCardShape, saveCardShapes, signUpCardShapes } = cardShapeDbSlice.actions;
export default cardShapeDbSlice.reducer;
